// Pure shaping for step 7's view: which group a capability lands in, how an
// assumption is bucketed, the headline tallies, and the customer-count line.
// Kept out of the components so the rules that decide what the PM sees are
// unit-tested and cannot drift between the three sections.

import type {
  AlignmentAssumption,
  AlignmentCapability,
  AlignmentProposalRow,
  MappingProposal,
  VerdictProposal,
} from '@/types/api';

export function formatCustomerCount(count?: number, total?: number): string | null {
  if (typeof count !== 'number' || count <= 0) return null;
  if (typeof total === 'number' && total > 0) return `${count} of ${total} customers`;
  return `${count} customers`;
}

// The strongest ask a capability answers: the need most customers raised.
export function askedForLine(cap: AlignmentCapability): string {
  if (cap.asks.length === 0) return 'No need asks for it';
  const top = cap.asks.reduce((best, a) => ((a.respondentCount ?? 0) > (best.respondentCount ?? 0) ? a : best), cap.asks[0]);
  const count = formatCustomerCount(top.respondentCount, top.respondentTotal);
  return count ? `Asked for by ${count}` : 'Asked for by customers';
}

export interface CapabilityGroups {
  core: AlignmentCapability[]; // in the idea, and customers asked for it
  plannedUnasked: AlignmentCapability[]; // in the idea, nobody asked
  askedUnplanned: AlignmentCapability[]; // asked for, not in the idea
  orphan: AlignmentCapability[]; // neither
}

// Capabilities with a pending mapping proposal sit in the Suggested block until
// confirmed, so they are left out of the groups.
export function groupCapabilities(capabilities: AlignmentCapability[], pendingCapabilityIds: Set<string>): CapabilityGroups {
  const groups: CapabilityGroups = { core: [], plannedUnasked: [], askedUnplanned: [], orphan: [] };
  for (const cap of capabilities) {
    if (pendingCapabilityIds.has(cap.nodeId)) continue;
    const asked = cap.asks.length > 0;
    if (cap.inIdea && asked) groups.core.push(cap);
    else if (cap.inIdea) groups.plannedUnasked.push(cap);
    else if (asked) groups.askedUnplanned.push(cap);
    else groups.orphan.push(cap);
  }
  return groups;
}

export interface AssumptionSplit {
  hardStops: AlignmentAssumption[];
  settled: AlignmentAssumption[]; // has a verdict; refuted first
  known: AlignmentAssumption[]; // settled by hand on the board (certainty known, no verdict)
  open: AlignmentAssumption[]; // eligible and not awaiting a decision
}

// A hard stop is the strongest statement about an assumption, so it wins the display;
// a stored verdict beats the board's known classification.
export function splitAssumptions(assumptions: AlignmentAssumption[], pendingAssumptionIds: Set<string>): AssumptionSplit {
  const split: AssumptionSplit = { hardStops: [], settled: [], known: [], open: [] };
  for (const a of assumptions) {
    if (a.hardStop) split.hardStops.push(a);
    else if (a.verdict) split.settled.push(a);
    else if (a.certainty === 'known') split.known.push(a);
    else if (a.eligible && !pendingAssumptionIds.has(a.nodeId)) split.open.push(a);
  }
  split.settled.sort((x, y) => Number(x.verdict!.status === 'confirmed') - Number(y.verdict!.status === 'confirmed'));
  return split;
}

export interface VerdictTally {
  confirmed: number;
  refuted: number;
  open: number; // every eligible assumption, pending or not
  refutedImportant: number; // refuted verdicts on critical assumptions: the pivot callout
  known: number;
}

export function tallyVerdicts(assumptions: AlignmentAssumption[]): VerdictTally {
  const tally: VerdictTally = { confirmed: 0, refuted: 0, open: 0, refutedImportant: 0, known: 0 };
  for (const a of assumptions) {
    if (a.hardStop) continue;
    if (a.verdict?.status === 'confirmed') tally.confirmed += 1;
    else if (a.verdict?.status === 'refuted') {
      tally.refuted += 1;
      if (a.riskLevel === 'critical') tally.refutedImportant += 1;
    } else if (a.certainty === 'known') tally.known += 1;
    else if (a.eligible) tally.open += 1;
  }
  return tally;
}

export function pendingMappings(row: AlignmentProposalRow | null | undefined): MappingProposal[] {
  return (row?.mappings ?? []).filter((p) => p.status === 'proposed');
}

export function pendingVerdicts(row: AlignmentProposalRow | null | undefined): VerdictProposal[] {
  return (row?.verdicts ?? []).filter((p) => p.status === 'proposed');
}
